// src/modules/feed/pages/FeedPage/index.js
import React, { useState, useEffect, useCallback } from 'react';
import { useWarmupTimer } from '../../../../shared/hooks/useWarmupTimer';
import { useInfiniteScroll } from '../../../../shared/hooks/useInfiniteScroll';
import { Container, Button, Image, Col, Spinner } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ActivitiesFeed from '../../components/ActivitiesFeed';
import Loading from '../../../../shared/components/Loading';
import ErrorState from '../../../../shared/components/ErrorState';
import { useService } from '../../../../shared/hooks/useService';
import './styles.css';

const FeedPage = () => {
  const { username } = useParams();
  const [feed, setFeed] = useState([]);
  const [nextPageToken, setNextPageToken] = useState();
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const service = useService();
  const { isWarmup } = useWarmupTimer(isLoading);

  useEffect(() => {
    const fetchFeed = async () => {
      try {
        if (username) {
          const response = await service.getActivities(undefined, undefined, username);
          setFeed(response.activities);
          setNextPageToken(response.nextPageToken);
          setHasMore(response.hasMore);

          // Fetch user profile data
          const profileResponse = await service.getProfile(username);
          setUser(profileResponse.user);
        } else {
          // Handle the case where username is not provided
          const response = await service.getActivities();
          const activitiesWithUserGivenName = response.activities.map(activity => ({
            ...activity,
            userGivenName: `${activity.firstName} ${activity.lastName}`
          }));
          setFeed(activitiesWithUserGivenName);
          setNextPageToken(response.nextPageToken);
          setHasMore(response.hasMore);
        }
        setIsLoading(false);
      } catch (error) {
        setError(error.message);
        setIsLoading(false);
      }
    };
    fetchFeed();
  }, [username, service]);

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    try {
      const response = await service.getActivities(nextPageToken, undefined, username);
      const newActivities = username
        ? response.activities
        : response.activities.map(activity => ({
            ...activity,
            userGivenName: `${activity.firstName} ${activity.lastName}`
          }));
      setFeed((prev) => [...prev, ...newActivities]);
      setNextPageToken(response.nextPageToken);
      setHasMore(response.hasMore);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, nextPageToken, username, service]);

  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: handleLoadMore,
    hasMore,
    isLoading: isLoadingMore,
    rootMargin: '200px'
  });

  const handleUsernameClick = (username) => {
    navigate(`/profile/${username}`);
  };

  if (username && !user) {
    return <Loading isWarmup={isWarmup} />;
  }

  if (username) {
    return (
      <Container className="feed-container">
        <div className="feed-header">
          <Col xs={8}>
            <Link to={`/profile/${user.username}`}>
              <Image
                src={user.profilePicture || '/default-avatar.png'}
                roundedCircle
                style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                alt={`${user.firstName}'s profile`}
              />
            </Link>
            <h2>Feed for {user.firstName} {user.lastName}</h2>
          </Col>
          <Col xs={4} className="d-flex justify-content-end">
            <Button
              variant="primary"
              onClick={() => navigate('/activity')}
            >
              Register Activity
            </Button>
          </Col>
        </div>

        <ActivitiesFeed
          activities={feed}
          onUsernameClick={handleUsernameClick}
        />
        {/* Infinite scroll sentinel and loading indicator */}
        <div ref={sentinelRef} className="scroll-sentinel" />
        {isLoadingMore && (
          <div className="loading-more-container">
            <Spinner animation="border" size="sm" className="me-2" />
            <span>Loading more activities...</span>
          </div>
        )}
        {!hasMore && feed.length > 0 && (
          <div className="end-of-feed">No more activities to load</div>
        )}
      </Container>
    );
  } else if (isLoading && feed.length === 0) {
    return <Loading isWarmup={isWarmup} />;
  } else if (error) {
    return (
      <ErrorState
        message={error}
        onRetry={() => window.location.reload()}
        onBack={() => navigate(-1)}
        title="Error Loading Activities"
      />
    );
  }

  // Render general feed
  return (
    <Container className="feed-container">
      <ActivitiesFeed
        activities={feed}
        onUsernameClick={handleUsernameClick}
      />
      {/* Infinite scroll sentinel and loading indicator */}
      <div ref={sentinelRef} className="scroll-sentinel" />
      {isLoadingMore && (
        <div className="loading-more-container">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>Loading more activities...</span>
        </div>
      )}
      {!hasMore && feed.length > 0 && (
        <div className="end-of-feed">No more activities to load</div>
      )}
    </Container>
  );
};

export default FeedPage;